import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Paper, 
  Typography, 
  CircularProgress, 
  Card, 
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  TextField
} from '@mui/material';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, PieChart, Pie, Cell } from 'recharts';
import GlobalConfigService from '../../services/global-config.service';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber';
import InventoryIcon from '@mui/icons-material/Inventory';
import PublicIcon from '@mui/icons-material/Public';

const Overview = () => {
  const [statistics, setStatistics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  useEffect(() => {
    fetchStatistics();
  }, [startDate, endDate]);

  const fetchStatistics = async () => {
    try {
      setLoading(true);
      const response = await GlobalConfigService.statistics(startDate, endDate);
      setStatistics(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh">
        <CircularProgress />
      </Box>
    );
  }

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  return (
    <Box sx={{ flexGrow: 1, p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Typography variant="h4">
          Tableau de Bord ({statistics.date_range.start_date} - {statistics.date_range.end_date})
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            label="Date de début"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Date de fin"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>

      {/* Cartes de statistiques globales */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#1976d2', color: 'white' }}>
            <CardContent>
              <ShoppingCartIcon sx={{ fontSize: 40 }} />
              <Typography variant="h6">Commandes Totales</Typography>
              <Typography variant="h4">{statistics.orders.global_stats.total_orders}</Typography>
              <Typography variant="subtitle2">
                Revenu: {statistics.orders.global_stats.total_revenue.toLocaleString()} FCFA
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#2e7d32', color: 'white' }}>
            <CardContent>
              <ConfirmationNumberIcon sx={{ fontSize: 40 }} />
              <Typography variant="h6">Tickets Vendus</Typography>
              <Typography variant="h4">{statistics.tickets.global_stats.total_quantity_sold}</Typography>
              <Typography variant="subtitle2">
                Revenu: {statistics.tickets.global_stats.total_revenue.toLocaleString()} FCFA
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#ed6c02', color: 'white' }}>
            <CardContent>
              <InventoryIcon sx={{ fontSize: 40 }} />
              <Typography variant="h6">Produits Vendus</Typography>
              <Typography variant="h4">{statistics.products.global_stats.total_quantity_sold}</Typography>
              <Typography variant="subtitle2">
                Revenu: {statistics.products.global_stats.total_revenue.toLocaleString()} FCFA
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: '#9c27b0', color: 'white' }}>
            <CardContent>
              <PublicIcon sx={{ fontSize: 40 }} />
              <Typography variant="h6">Commandes en Attente</Typography>
              <Typography variant="h4">{statistics.orders.global_stats.pending_orders}</Typography>
              <Typography variant="subtitle2">
                Valeur: {statistics.orders.global_stats.pending_revenue.toLocaleString()} FCFA
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Onglets pour les différentes sections */}
      <Paper sx={{ width: '100%', mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} centered>
          <Tab label="Produits" />
          <Tab label="Tickets" />
          <Tab label="Commandes" />
        </Tabs>
      </Paper>

      {/* Contenu des onglets */}
      {tabValue === 0 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Top 5 Produits</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Prix</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Revenu</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.products.top_products.map((product) => (
                      <TableRow key={product.name}>
                        <TableCell>{product.name}</TableCell>
                        <TableCell align="right">{parseInt(product.price).toLocaleString()} FCFA</TableCell>
                        <TableCell align="right">{product.quantity_sold}</TableCell>
                        <TableCell align="right">{product.revenue.toLocaleString()} FCFA</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Ventes par Catégorie</Typography>
              <PieChart width={500} height={300}>
                <Pie
                  data={statistics.products.sales_by_category}
                  cx={250}
                  cy={150}
                  labelLine={false}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="revenue"
                  nameKey="category"
                >
                  {statistics.products.sales_by_category.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 1 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Ventes de Tickets par Type</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Type</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Revenu</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.tickets.sales_by_type.map((ticket) => (
                      <TableRow key={ticket.type}>
                        <TableCell>{ticket.type}</TableCell>
                        <TableCell align="right">{ticket.quantity_sold}</TableCell>
                        <TableCell align="right">{ticket.revenue.toLocaleString()} FCFA</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Top Tickets</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Ticket</TableCell>
                      <TableCell align="right">Prix</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Revenu</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.tickets.top_tickets.map((ticket) => (
                      <TableRow key={ticket.name}>
                        <TableCell>{ticket.name}</TableCell>
                        <TableCell align="right">{parseInt(ticket.price).toLocaleString()} FCFA</TableCell>
                        <TableCell align="right">{ticket.quantity_sold}</TableCell>
                        <TableCell align="right">{ticket.revenue.toLocaleString()} FCFA</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}

      {tabValue === 2 && (
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Commandes par Pays</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Pays</TableCell>
                      <TableCell align="right">Commandes</TableCell>
                      <TableCell align="right">Revenu</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.orders.top_countries.map((country) => (
                      <TableRow key={country.country}>
                        <TableCell>{country.country}</TableCell>
                        <TableCell align="right">{country.order_count}</TableCell>
                        <TableCell align="right">{country.revenue.toLocaleString()} FCFA</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="h6" gutterBottom>Méthodes de Paiement</Typography>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Méthode</TableCell>
                      <TableCell align="right">Commandes</TableCell>
                      <TableCell align="right">Revenu</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {statistics.orders.by_payment_method.map((method) => (
                      <TableRow key={method.payment_method}>
                        <TableCell>{method.payment_method}</TableCell>
                        <TableCell align="right">{method.order_count}</TableCell>
                        <TableCell align="right">{method.revenue.toLocaleString()} FCFA</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default Overview;